import { useState } from 'react';
import { Building2 } from 'lucide-react';
import { ModalPanel, Button, Select } from '@/contasis';
import { useVentanasIniciales } from '@/hooks/useVentanasIniciales';
import { useWorkspace } from '@/contexts/ContextoWorkspace';
import { useToast } from '@/contexts/ContextoToast';
import { useEmpresas } from '@/hooks/useEmpresas';
import { REGIMENES_TRIBUTARIOS } from '@/pages/Private/Features/Configuraciones/Features/ConfiguracionEmpresas/CrearConfiguracionEmpresa';
import type { UpdateEmpresaDTO } from '@/services/api/empresas.service';

export const ModalRegimenTributario = () => {
  const { currentEmpresa } = useWorkspace();
  const { actualizarEmpresa, esGuardando } = useEmpresas();
  const { showSuccess, showError } = useToast();
  const [regimen, setRegimen] = useState('');

  const { mostrarRegimen } = useVentanasIniciales();
  const debeMostrarse = mostrarRegimen;

  if (!debeMostrarse || !currentEmpresa) return null;

  const handleConfirmar = async () => {
    if (!regimen) {
      showError('Selecciona un régimen tributario');
      return;
    }

    const dto: UpdateEmpresaDTO = {
      id: currentEmpresa.id,
      ruc: currentEmpresa.ruc,
      razonSocial: currentEmpresa.razonSocial,
      nombreComercial: currentEmpresa.nombreComercial,
      direccionFiscal: currentEmpresa.direccionFiscal,
      codigoDistrito: currentEmpresa.codigoDistrito,
      distrito: currentEmpresa.distrito,
      codigoProvincia: currentEmpresa.codigoProvincia,
      provincia: currentEmpresa.provincia,
      codigoDepartamento: currentEmpresa.codigoDepartamento,
      departamento: currentEmpresa.departamento,
      codigoPostal: currentEmpresa.codigoPostal,
      telefonos: currentEmpresa.telefonos,
      correosElectronicos: currentEmpresa.correosElectronicos,
      sitioWeb: currentEmpresa.sitioWeb,
      textoPiePagina: currentEmpresa.textoPiePagina,
      actividadEconomica: currentEmpresa.actividadEconomica,
      regimenTributario: regimen,
      monedaBase: currentEmpresa.monedaBase,
      representanteLegal: currentEmpresa.representanteLegal,
      nombreRepresentanteLegal: currentEmpresa.nombreRepresentanteLegal,
      tipoDocumentoRepresentante: currentEmpresa.tipoDocumentoRepresentante,
      numeroDocumentoRepresentante: currentEmpresa.numeroDocumentoRepresentante,
      ambienteSunat: currentEmpresa.ambienteSunat,
      facturarEn: currentEmpresa.facturarEn,
      esActivo: currentEmpresa.esActivo,
      esPrincipal: currentEmpresa.esPrincipal,
    };

    try {
      await actualizarEmpresa(dto);
      showSuccess('Régimen tributario configurado correctamente');
    } catch {
      showError('No se pudo guardar el régimen tributario. Inténtalo de nuevo.');
    }
  };

  return (
    <ModalPanel
      isOpen
      title="Configura tu régimen tributario"
      subtitle="Este dato es obligatorio para continuar usando Senciyo"
      size="small"
      onClose={() => {}}
      closeOnClickOutside={false}
      closeOnEsc={false}
      disableClose
      footerActions={
        <Button variant="primary" size="sm" loading={esGuardando} onClick={() => void handleConfirmar()}>
          Guardar y continuar
        </Button>
      }
    >
      <div className="px-6 py-8 flex flex-col items-center text-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-brand-light flex items-center justify-center">
          <Building2 className="w-8 h-8 text-brand" />
        </div>
        <div className="space-y-1.5 max-w-sm">
          <h3 className="font-display text-h5 text-primary">Selecciona el régimen tributario de {currentEmpresa.razonSocial}</h3>
          <p className="text-ui-sm text-secondary">
            Necesitamos este dato para emitir tus comprobantes electrónicos correctamente.
          </p>
        </div>
        <div className="w-full text-left">
          <Select
            name="regimenTributario"
            value={regimen}
            onChange={e => setRegimen(e.target.value)}
            options={REGIMENES_TRIBUTARIOS}
          />
        </div>
      </div>
    </ModalPanel>
  );
};
